import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { formatINR, formatDate, statusTone } from "@/lib/format";

export default function FreelancerDetail() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [perf, setPerf] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [fr, wo, pa, pr] = await Promise.all([
        api.get(`/freelancers/${id}`),
        api.get(`/work-orders`),
        api.get(`/payments`),
        api.get(`/performance?freelancer_id=${id}`),
      ]);
      setF(fr.data);
      setOrders(wo.data.filter((o) => o.freelancer_id === id));
      setPayments(pa.data.filter((p) => p.freelancer_id === id));
      setPerf(pr.data);
    };
    load();
  }, [id]);

  if (!f) return <div className="p-8 text-neutral-500 font-mono-tight text-sm">Loading…</div>;

  return (
    <div>
      <PageHeader
        title={f.name}
        description={<Link to="/freelancers" className="underline decoration-2 underline-offset-4">← Back to freelancers</Link>}
      />
      <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-neutral-200 bg-white p-6" data-testid="profile-card">
          <div className="label-caps">Profile</div>
          <div className="mt-3 space-y-2 text-sm">
            <div><span className="text-neutral-500">Email · </span>{f.email}</div>
            <div><span className="text-neutral-500">Phone · </span>{f.phone || "—"}</div>
            <div><span className="text-neutral-500">Category · </span>{f.category}</div>
            <div><span className="text-neutral-500">Skills · </span>{(f.skills || []).join(", ")}</div>
            <div><span className="text-neutral-500">Rating · </span><span className="font-mono-tight">{f.rating}</span></div>
            <div><span className="text-neutral-500">Status · </span><span className={`px-2 py-0.5 text-xs font-mono-tight uppercase ${statusTone(f.status)}`}>{f.status}</span></div>
            <div><span className="text-neutral-500">UPI · </span>{f.payment_details?.upi || "—"}</div>
          </div>
        </div>
        <div className="border border-neutral-200 bg-white md:col-span-2">
          <div className="px-4 py-3 border-b border-neutral-200 label-caps">Work History</div>
          <table className="fms-table w-full"><thead><tr><th>Title</th><th>Status</th><th>Rate</th><th>Deadline</th></tr></thead>
            <tbody>
              {orders.map((o) => (<tr key={o.id}><td>{o.title}</td><td className="font-mono-tight text-xs">{o.status}</td><td className="font-mono-tight num">{formatINR(o.rate)}</td><td className="font-mono-tight text-neutral-500">{o.deadline || "—"}</td></tr>))}
              {orders.length === 0 && <tr><td colSpan={4} className="text-center text-neutral-500 py-6">No orders.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="border border-neutral-200 bg-white md:col-span-2">
          <div className="px-4 py-3 border-b border-neutral-200 label-caps">Payments</div>
          <table className="fms-table w-full"><thead><tr><th>Invoice</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {payments.map((p) => (<tr key={p.id}><td className="font-mono-tight">{p.invoice_number}</td><td className="font-mono-tight num">{formatINR(p.amount)}</td><td className="font-mono-tight text-xs">{p.status}</td><td className="font-mono-tight text-neutral-500">{formatDate(p.payment_date)}</td></tr>))}
              {payments.length === 0 && <tr><td colSpan={4} className="text-center text-neutral-500 py-6">No payments.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="border border-neutral-200 bg-white">
          <div className="px-4 py-3 border-b border-neutral-200 label-caps">Performance</div>
          <div className="p-4 space-y-3">
            {perf.map((r) => (
              <div key={r.id} className="border-b border-neutral-200 pb-2">
                <div className="flex justify-between text-sm">
                  <div className="font-mono-tight">Q {r.quality_score} · OT {r.on_time_score}</div>
                  <div className="text-xs text-neutral-500">{formatDate(r.review_date)}</div>
                </div>
                {r.reviewer_notes && <div className="text-xs text-neutral-600 mt-1">{r.reviewer_notes}</div>}
              </div>
            ))}
            {perf.length === 0 && <div className="text-sm text-neutral-500">No reviews yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
